import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, Grid, IconButton, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { hasSelfReflectionScore } from './selfReflectionModel';
import SelfReflectionTrainingRecommendations from './SelfReflectionTrainingRecommendations';

export const SELF_REFLECTION_MODES = [
  { value: 'self', label: 'Eigene Einschätzung' },
  { value: 'instructor', label: 'Lehrhöhenretter' },
  { value: 'comparison', label: 'Direkter Vergleich' },
  { value: 'combined', label: '50:50-Verrechnung' },
];

const PARTICIPANT_COLORS = [
  '#1565C0', '#C62828', '#2E7D32', '#6A1B9A', '#EF6C00',
  '#00838F', '#AD1457', '#4527A0', '#558B2F', '#D84315',
  '#0277BD', '#9E9D24', '#5D4037', '#00695C', '#7B1FA2',
  '#283593', '#00897B', '#F4511E', '#3949AB', '#7CB342',
];

function flattenCriteria(categories = []) {
  return categories.flatMap((category) => category.criteria.flatMap((criterion) => (
    criterion.type === 'group' ? criterion.criteria : [criterion]
  )));
}

function getParticipantColor(index) {
  if (index < PARTICIPANT_COLORS.length) return PARTICIPANT_COLORS[index];
  return `hsl(${Math.round((index * 137.508) % 360)} 68% 38%)`;
}

function prepareMarkerPositions(markers) {
  const scoreDepths = new Map();
  return markers
    .filter((marker) => hasSelfReflectionScore(marker.score))
    .map((marker) => {
      const scoreKey = Number(marker.score).toFixed(2);
      const lane = scoreDepths.get(scoreKey) || 0;
      scoreDepths.set(scoreKey, lane + 1);
      return { ...marker, lane };
    });
}

function ScoreBar({ primary, secondary = null, markers = [], primaryLabel = null }) {
  const percent = (score) => ((Number(score) - 1) / 3) * 100;
  const positionedMarkers = prepareMarkerPositions(markers);
  const stackDepth = positionedMarkers.reduce((maximum, marker) => Math.max(maximum, marker.lane + 1), 0);
  const barTop = 12 + stackDepth * 6;
  return (
    <Box sx={{ position: 'relative', pt: `${barTop}px`, pb: 2.5 }}>
      <Box sx={{ height: 10, borderRadius: 999, background: 'linear-gradient(90deg, #C43B30, #E9B522 52%, #357F32)' }} />
      {positionedMarkers.map((marker) => (
        <Tooltip key={marker.id} title={`${marker.label}: ${Number(marker.score).toLocaleString('de-DE', { maximumFractionDigits: 2 })}`} arrow>
          <Box
            component="span"
            aria-label={`${marker.label}: ${marker.score}`}
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: barTop - 15 - (marker.lane * 6),
              left: `calc(${percent(marker.score)}% - 6px)`,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: marker.color,
              border: '2px solid white',
              boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.45)',
            }}
          />
        </Tooltip>
      ))}
      {hasSelfReflectionScore(primary) && (
        <Tooltip title={primaryLabel ? `${primaryLabel}: ${Number(primary).toLocaleString('de-DE', { maximumFractionDigits: 2 })}` : ''} disableHoverListener={!primaryLabel} arrow>
          <Box component="span" aria-label={primaryLabel ? `${primaryLabel}: ${primary}` : undefined} sx={{ position: 'absolute', zIndex: 3, top: barTop - 3, left: `calc(${percent(primary)}% - 8px)`, width: 16, height: 16, borderRadius: '50%', bgcolor: 'white', border: '3px solid', borderColor: 'primary.main' }} />
        </Tooltip>
      )}
      {hasSelfReflectionScore(secondary) && (
        <Box sx={{ position: 'absolute', zIndex: 2, top: barTop - 2, left: `calc(${percent(secondary)}% - 7px)`, width: 14, height: 14, borderRadius: '50%', bgcolor: 'secondary.main', border: '2px solid white', boxShadow: 1 }} />
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.6 }}>
        {[1, 2, 3, 4].map((score) => <Typography key={score} variant="caption">{score}</Typography>)}
      </Stack>
    </Box>
  );
}

function GroupMarkerLegend({ participants }) {
  return (
    <Alert severity="info" icon={false} sx={{ py: 0.75 }}>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box aria-hidden sx={{ width: 16, height: 16, flexShrink: 0, borderRadius: '50%', bgcolor: 'white', border: '3px solid', borderColor: 'primary.main' }} />
          <Typography variant="body2">Gruppenmittel</Typography>
        </Stack>
        {participants.map((participant) => (
          <Stack key={participant.id} direction="row" spacing={0.75} alignItems="center">
            <Box aria-hidden sx={{ width: 12, height: 12, flexShrink: 0, borderRadius: '50%', bgcolor: participant.color, border: '2px solid white', boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.45)' }} />
            <Typography variant="body2">{participant.label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Alert>
  );
}

function ComparisonLegend() {
  return (
    <Alert severity="info" icon={false} sx={{ py: 0.75 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 2 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box aria-hidden sx={{ width: 16, height: 16, flexShrink: 0, borderRadius: '50%', bgcolor: 'white', border: '3px solid', borderColor: 'primary.main' }} />
          <Typography variant="body2">Selbsteinschätzung</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box aria-hidden sx={{ width: 14, height: 14, flexShrink: 0, borderRadius: '50%', bgcolor: 'secondary.main', border: '2px solid white', boxShadow: 1 }} />
          <Typography variant="body2">Lehrhöhenretter</Typography>
        </Stack>
      </Stack>
    </Alert>
  );
}

function getRankedEntries(summary, criteria, direction) {
  const entries = criteria
    .map((criterion, index) => {
      const rawValue = summary?.values?.[criterion.key];
      const score = rawValue !== null && typeof rawValue === 'object' ? rawValue.score : rawValue;
      return { key: criterion.key, label: criterion.label, score, index };
    })
    .filter((entry) => hasSelfReflectionScore(entry.score))
    .map((entry) => ({ ...entry, score: Number(entry.score) }))
    .sort((left, right) => direction * (left.score - right.score) || left.index - right.index);
  if (!entries.length) return [];

  const cutoffScore = entries[Math.min(1, entries.length - 1)].score;
  const rankedEntries = entries.filter((entry) => (
    direction < 0 ? entry.score >= cutoffScore : entry.score <= cutoffScore
  ));
  return (rankedEntries.length > 2 ? rankedEntries : entries.slice(0, 2))
    .map(({ index, ...entry }) => entry);
}

function Ranking({ title, entries = [], color = 'default' }) {
  const [expanded, setExpanded] = useState(false);
  const hasLargeTie = entries.length > 2;
  return (
    <Stack spacing={0.6}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {!entries.length && <Chip size="small" label="Keine Daten" />}
        {hasLargeTie && !expanded && <Chip size="small" color={color} label=">2 Kategorien" clickable onClick={() => setExpanded(true)} />}
        {hasLargeTie && expanded && <Chip size="small" variant="outlined" color={color} label="Kategorien einklappen" clickable onClick={() => setExpanded(false)} />}
        {(!hasLargeTie || expanded) && entries.map((entry) => <Chip key={entry.key} size="small" color={color} label={`${entry.label}: ${entry.score}`} />)}
      </Stack>
    </Stack>
  );
}

function CriterionResult({ criterion, analysis, mode, source, eligibleAssessorCount, groupView, participants }) {
  const comparison = analysis.comparison?.[criterion.key] || {};
  const primary = mode === 'comparison'
    ? comparison.selfScore
    : (mode === 'instructor' ? analysis.instructor.values?.[criterion.key]?.score : source?.values?.[criterion.key]);
  const secondary = mode === 'comparison' ? comparison.instructorScore : null;
  const responseCount = analysis.instructor.values?.[criterion.key]?.responseCount || 0;
  const applicableParticipants = groupView
    ? participants.filter((participant) => participant.criterionKeys.includes(criterion.key))
    : [];
  const participantMarkers = applicableParticipants
    .map((participant) => ({ ...participant, score: participant.values[criterion.key] }))
    .filter((participant) => hasSelfReflectionScore(participant.score));

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, width: '100%', height: '100%', boxSizing: 'border-box' }}>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{criterion.label}</Typography>
          {criterion.infoText && (
            <Tooltip title={criterion.infoText} arrow>
              <IconButton size="small" aria-label={`Hinweis zu ${criterion.label}`}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {groupView && (
          <Typography variant="caption" color="text.secondary">
            {participantMarkers.length}/{applicableParticipants.length} Teilnehmer berücksichtigt
          </Typography>
        )}
        {!groupView && (mode === 'instructor' || mode === 'comparison' || mode === 'combined') && (
          <Typography variant="caption" color="text.secondary">
            {Number.isInteger(eligibleAssessorCount)
              ? `${responseCount}/${eligibleAssessorCount} Lehrhöhenretter Einschätzungen`
              : `${responseCount} verwertbare Aussage${responseCount === 1 ? '' : 'n'}`}
          </Typography>
        )}
        <ScoreBar primary={primary} secondary={secondary} markers={participantMarkers} primaryLabel={groupView ? 'Gruppenmittel' : null} />
      </Stack>
    </Paper>
  );
}

export default function SelfReflectionResults({ config, analysis, individuals = [], eligibleAssessorCount, mode, onModeChange, fixedMode = false, groupView = false, title = 'Einschätzungen', headerAction = null }) {
  const criteria = useMemo(() => flattenCriteria(config?.categories), [config]);
  const participants = useMemo(() => [...individuals]
    .sort((left, right) => String(left.subject?.fullName || '').localeCompare(String(right.subject?.fullName || ''), 'de-DE'))
    .map((individual, index) => ({
      id: Number(individual.subject?.id),
      label: individual.subject?.fullName || `Teilnehmer ${index + 1}`,
      color: getParticipantColor(index),
      criterionKeys: individual.criterionKeys || [],
      overallAverage: individual.analysis?.combined?.overallAverage,
      values: individual.analysis?.combined?.values || {},
    })), [individuals]);
  if (!analysis) return null;

  const source = mode === 'instructor' ? analysis.instructor : mode === 'combined' ? analysis.combined : analysis.self;
  return (
    <Box component="section" sx={{ p: { xs: 1.5, sm: 2 }, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          {headerAction}
        </Stack>
        {!fixedMode && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={mode}
            onChange={(_, value) => value && onModeChange(value)}
            size="small"
            sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, '& .MuiToggleButton-root': { flex: { xs: '1 1 45%', md: 1 } } }}
          >
            {SELF_REFLECTION_MODES.map((option) => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
          </ToggleButtonGroup>
        )}
        {mode === 'combined' && Array.isArray(analysis.trainingRecommendations) && (
          <SelfReflectionTrainingRecommendations recommendations={analysis.trainingRecommendations} />
        )}
        {groupView && <GroupMarkerLegend participants={participants} />}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {mode === 'comparison' ? 'Direkter Vergleich' : `Gesamtstand: ${source?.overallAverage ?? 'Keine Daten'}`}
            </Typography>
            {mode !== 'comparison' && (
              <ScoreBar
                primary={source?.overallAverage}
                markers={groupView ? participants.map((participant) => ({ ...participant, score: participant.overallAverage })) : []}
                primaryLabel={groupView ? 'Gruppenmittel' : null}
              />
            )}
            {mode === 'comparison' && (
              <Stack spacing={0.5}>
                <ComparisonLegend />
                <ScoreBar primary={analysis.self?.overallAverage} secondary={analysis.instructor?.overallAverage} />
              </Stack>
            )}
            {mode === 'comparison' ? (
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 6 }}><Ranking title="Eigene Stärke" entries={getRankedEntries(analysis.self, criteria, -1)} color="success" /><Ranking title="Eigene Schwäche" entries={getRankedEntries(analysis.self, criteria, 1)} color="warning" /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><Ranking title="Stärke laut Lehrhöhenrettern" entries={getRankedEntries(analysis.instructor, criteria, -1)} color="success" /><Ranking title="Schwäche laut Lehrhöhenrettern" entries={getRankedEntries(analysis.instructor, criteria, 1)} color="warning" /></Grid>
              </Grid>
            ) : (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Ranking title="Stärke" entries={getRankedEntries(source, criteria, -1)} color="success" />
                <Ranking title="Schwäche" entries={getRankedEntries(source, criteria, 1)} color="warning" />
              </Stack>
            )}
          </Stack>
        </Paper>

        <Stack spacing={2.5}>
          {(config?.categories || []).map((category) => (
            <Stack key={category.key} spacing={1.25}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{category.label}</Typography>
              <Grid container spacing={1.25}>
                {category.criteria.map((criterion) => {
                  if (criterion.type === 'group') {
                    return (
                      <Grid size={12} key={criterion.key}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{criterion.label}</Typography>
                          <Grid container spacing={1.25}>
                            {criterion.criteria.map((child) => (
                              <Grid size={{ xs: 12, md: 6 }} key={child.key} sx={{ display: 'flex' }}>
                                <CriterionResult criterion={child} analysis={analysis} eligibleAssessorCount={eligibleAssessorCount} mode={mode} source={source} groupView={groupView} participants={participants} />
                              </Grid>
                            ))}
                          </Grid>
                        </Stack>
                      </Grid>
                    );
                  }
                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={criterion.key} sx={{ display: 'flex' }}>
                      <CriterionResult criterion={criterion} analysis={analysis} eligibleAssessorCount={eligibleAssessorCount} mode={mode} source={source} groupView={groupView} participants={participants} />
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}